/**
 * The school's booking fee on an order's contract base (spec §4.2):
 * `round-half-up(base × percent) + fixed`, in minor units. Shared by the API
 * (the charged figure, `ComputeBookingFee`) and the checkouts (the
 * first-paint estimate, `estimateBookingFeePence` in @baluu/ui) so the two
 * can never round differently. `Math.round` is half-up for the non-negative
 * values this ever sees.
 */
export type BookingFeeRateLike = {
  /** Basis points: 150 = 1.5%. */
  percentBps: number;
  /** Minor units: 20 = £0.20. */
  fixedMinor: number;
};

export function computeBookingFee(
  baseMinor: number,
  rate: BookingFeeRateLike | null | undefined,
): number {
  if (!rate) return 0;
  if (!Number.isFinite(baseMinor) || baseMinor <= 0) return 0;
  const pct = Math.round((baseMinor * rate.percentBps) / 10000);
  return pct + rate.fixedMinor;
}

type PartnerSnapshotItem = {
  price?: number | null;
  quantity?: number | null;
  isDeposit?: boolean;
  remainingBalance?: number | null;
  contractTotal?: number | null;
  depositPaid?: number | null;
};

const finite = (n: unknown): number =>
  typeof n === "number" && Number.isFinite(n) ? n : 0;

/**
 * What a first-payment partner order (staff / assistant link, request-to-book
 * confirmation link) still owes after this charge — the part of its
 * booking-fee base beyond the charge now. Shared by the API
 * (`partnerOrderBookingFeeBasePence`) and the widget's first-paint estimate.
 *
 * Two snapshot shapes carry a later balance:
 * - `isDeposit` + `remainingBalance` (the widget's deposit split shape);
 * - `contractTotal` + `depositPaid` (buildCreationOrderItem):
 *   later = contractTotal − depositPaid − this line's charge, floored at 0.
 * A full-payment link carries neither, so it owes nothing later.
 */
export function partnerOrderLaterBalanceMinor(snapshotItems: unknown): number {
  const items = Array.isArray(snapshotItems)
    ? (snapshotItems as Array<PartnerSnapshotItem | null>)
    : [];
  return items.reduce((sum, i) => {
    if (!i) return sum;
    if (i.isDeposit) return sum + Math.max(0, finite(i.remainingBalance));
    if (typeof i.contractTotal === "number" && Number.isFinite(i.contractTotal)) {
      const quantity = finite(i.quantity) > 0 ? finite(i.quantity) : 1;
      return sum + Math.max(0, i.contractTotal - finite(i.depositPaid) - finite(i.price) * quantity);
    }
    return sum;
  }, 0);
}
